import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { usePOAs } from '../../../hooks/usePOAs'

const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
const LABEL_CLS = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'

export default function POAEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { poas, loading, updatePOA } = usePOAs()
  const poa = poas.find(p => p.id === id)

  const [formData, setFormData] = useState({
    importer_name: '',
    eori_number: '',
    vat_number: '',
    signatory_name: '',
    importer_country: 'GB',
    importer_address: '',
    agent_name: '',
    signed_date: '',
    is_open_ended: false,
    valid_until: '',
    is_uk_local_company: false,
    is_pva: false,
    is_cds_approved: false,
    notes: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Pre-fill once the POA is available
  useEffect(() => {
    if (poa) {
      setFormData({
        importer_name:      poa.importer_name ?? '',
        eori_number:        poa.eori_number ?? '',
        vat_number:         poa.vat_number ?? '',
        signatory_name:     poa.signatory_name ?? '',
        importer_country:   poa.importer_country ?? 'GB',
        importer_address:   poa.importer_address ?? '',
        agent_name:         poa.agent_name ?? '',
        signed_date:        poa.signed_date ?? '',
        is_open_ended:      poa.is_open_ended,
        valid_until:        poa.valid_until ?? '',
        is_uk_local_company: poa.is_uk_local_company,
        is_pva:             poa.is_pva,
        is_cds_approved:    poa.is_cds_approved,
        notes:              poa.notes ?? '',
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poa?.id])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!id) return
    setSubmitting(true)
    setError(null)
    try {
      await updatePOA(id, {
        importer_name:       formData.importer_name,
        eori_number:         formData.eori_number || null,
        vat_number:          formData.vat_number || null,
        signatory_name:      formData.signatory_name || null,
        importer_country:    formData.importer_country || null,
        importer_address:    formData.importer_address || null,
        agent_name:          formData.agent_name || null,
        signed_date:         formData.signed_date || null,
        is_open_ended:       formData.is_open_ended,
        valid_until:         formData.is_open_ended ? null : (formData.valid_until || null),
        is_uk_local_company: formData.is_uk_local_company,
        is_pva:              formData.is_pva,
        is_cds_approved:     formData.is_cds_approved,
        notes:               formData.notes || null,
      })
      navigate(`/customs/poa/${id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update POA')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">Loading...</p>
      </div>
    )
  }

  if (!poa) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">POA not found.</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-3xl">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-6">
          <button onClick={() => navigate('/customs/poa')}
            className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
            Customs
          </button>
          <span>/</span>
          <button onClick={() => navigate('/customs/poa')}
            className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
            POA Management
          </button>
          <span>/</span>
          <button onClick={() => navigate(`/customs/poa/${id}`)}
            className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
            {poa.importer_name}
          </button>
          <span>/</span>
          <span className="text-gray-900 dark:text-white">Edit</span>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Edit POA</h1>

        <form onSubmit={handleSubmit}
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
          <div className="grid grid-cols-2 gap-4">

            {/* Importer Name — full width, required */}
            <div className="col-span-2">
              <label className={LABEL_CLS}>Importer Name <span className="text-red-500">*</span></label>
              <input type="text" required value={formData.importer_name}
                onChange={e => setFormData(f => ({ ...f, importer_name: e.target.value }))}
                className={INPUT_CLS} placeholder="e.g. Acme Imports Ltd" />
            </div>

            {/* EORI */}
            <div>
              <label className={LABEL_CLS}>EORI Number</label>
              <input type="text" value={formData.eori_number}
                onChange={e => setFormData(f => ({ ...f, eori_number: e.target.value }))}
                className={INPUT_CLS} placeholder="e.g. GB123456789000" />
            </div>

            {/* VAT */}
            <div>
              <label className={LABEL_CLS}>VAT Number</label>
              <input type="text" value={formData.vat_number}
                onChange={e => setFormData(f => ({ ...f, vat_number: e.target.value }))}
                className={INPUT_CLS} placeholder="e.g. GB123456789" />
            </div>

            {/* Signatory Name */}
            <div>
              <label className={LABEL_CLS}>Signatory Name</label>
              <input type="text" value={formData.signatory_name}
                onChange={e => setFormData(f => ({ ...f, signatory_name: e.target.value }))}
                className={INPUT_CLS} placeholder="Full name of signatory" />
            </div>

            {/* Country */}
            <div>
              <label className={LABEL_CLS}>Country</label>
              <input type="text" value={formData.importer_country}
                onChange={e => setFormData(f => ({ ...f, importer_country: e.target.value }))}
                className={INPUT_CLS} placeholder="GB" />
            </div>

            {/* Importer Address — full width */}
            <div className="col-span-2">
              <label className={LABEL_CLS}>Importer Address</label>
              <textarea value={formData.importer_address}
                onChange={e => setFormData(f => ({ ...f, importer_address: e.target.value }))}
                rows={2} className={INPUT_CLS + ' resize-none'}
                placeholder="Street, City, Postcode" />
            </div>

            {/* Agent Name — full width */}
            <div className="col-span-2">
              <label className={LABEL_CLS}>Agent Name</label>
              <input type="text" value={formData.agent_name}
                onChange={e => setFormData(f => ({ ...f, agent_name: e.target.value }))}
                className={INPUT_CLS} placeholder="Customs agent / broker name" />
            </div>

            {/* Signed Date */}
            <div>
              <label className={LABEL_CLS}>Signed Date</label>
              <input type="date" value={formData.signed_date}
                onChange={e => setFormData(f => ({ ...f, signed_date: e.target.value }))}
                className={INPUT_CLS} />
            </div>

            {/* Valid Until — disabled when open-ended */}
            <div>
              <label className={LABEL_CLS + (formData.is_open_ended ? ' opacity-40' : '')}>
                Valid Until
              </label>
              <input type="date" value={formData.valid_until}
                disabled={formData.is_open_ended}
                onChange={e => setFormData(f => ({ ...f, valid_until: e.target.value }))}
                className={INPUT_CLS + (formData.is_open_ended ? ' opacity-40 cursor-not-allowed' : '')} />
            </div>

            {/* Checkboxes — 2×2 grid */}
            <div className="col-span-2 grid grid-cols-2 gap-y-3">
              {([
                { key: 'is_open_ended',       label: 'Open-ended (no expiry)' },
                { key: 'is_uk_local_company',  label: 'UK Local Company' },
                { key: 'is_pva',               label: 'PVA Authorisation' },
                { key: 'is_cds_approved',      label: 'CDS Approved' },
              ] as const).map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox"
                    checked={formData[key]}
                    onChange={e => setFormData(f => ({ ...f, [key]: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                </label>
              ))}
            </div>

            {/* Notes — full width */}
            <div className="col-span-2">
              <label className={LABEL_CLS}>Notes</label>
              <textarea value={formData.notes}
                onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                rows={3} className={INPUT_CLS + ' resize-y'}
                placeholder="Additional notes..." />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 rounded text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button type="button"
              onClick={() => navigate(`/customs/poa/${id}`)}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md transition-colors"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
